import type { z } from 'zod'

import type { InventoryFilters, InventoryGroupFilters, UnitListingFilters } from '@/lib/api/client/inventory'
import type { InventoryIncludeFacet, InventoryOrderGroupSort, InventoryPageSort, LocalStockState } from '@/lib/api/schemas/inventory.schema'
import {
  InventoryDiscrepancyReasonSchema,
  LocalStockStateSchema,
  MarketplaceBindingSchema,
  MatchBucketSchema,
  MissingTaxonomyFacetSchema,
  InventoryGroupLevelSchema,
  UnitLifecycleScopeSchema,
  InventoryRowGrainSchema,
  DescriptionPresenceSchema,
  InventoryIncludeFacetSchema,
} from '@/lib/api/schemas/inventory.schema'
import type { InventoryGroupLevel, UnitLifecycleScope } from '@/lib/api/schemas/inventory.schema'
import { isViewPresetKey, RETIRED_YMARKET_PRICES_VIEW, type ViewPresetKey } from '@/lib/views/presets'
import { YmEconomicsFilterSchema, YmPriceAttractivenessLevelSchema } from '@/lib/api/schemas/ymarket-pricing.schema'
import { warehouseProductStatusLabels } from '@/lib/warehouse/labels'

export type WarehouseGrouping = 'none' | InventoryGroupLevel
export type MarketSortColumn = 'ymarketSellsFor' | 'ymarketPromo' | 'ymarketBoost' | 'ymarketRubPerGram' | 'ymarketPurchaseCost' | 'ymarketEarnings' | 'ymarketEarningsPercent' | 'ymarketNetPerGram' | 'ymarketPriceForTarget' | 'ymarketShowcase' | 'ymarketShowcasePerGram' | 'ymarketShowcaseThresholdRatio'
export type OrderSortColumn = 'orderNumber' | 'orderCreatedAt' | 'buyer' | 'orderStatus' | 'city' | 'orderAmount'
export type PieceSortColumn = 'uin' | 'article' | 'title' | 'product' | 'weight' | 'lifecycle' | 'activity' | 'size' | 'photoCount' | 'onecPrice' | 'wildberriesPrice' | 'ymarketPrice' | 'ozonPrice' | 'oriSitePrice' | MarketSortColumn
export type WarehouseSortColumn = PieceSortColumn | OrderSortColumn
export type StorefrontSortColumn = 'uin' | 'article' | 'title' | 'weight' | 'lifecycle' | 'activity' | 'size' | 'photoCount' | 'onecPrice' | 'wildberriesPrice' | 'ymarketPrice' | 'ozonPrice' | 'oriSitePrice'
export type WarehouseSortDirection = 'ascending' | 'descending'

export interface WarehouseSort {
  column: WarehouseSortColumn
  direction: WarehouseSortDirection
}

export interface WarehouseUrlState {
  view?: ViewPresetKey
  include?: InventoryIncludeFacet[]
  filters: InventoryFilters
  sort: WarehouseSort
  page: number
  pageSize: number
  selectedVariantId: string | null
  selectedOrderId: number | null
  grouping: WarehouseGrouping
  lifecycle: UnitLifecycleScope
  withPhotos: boolean
  /** The warehouse-only filters «Заказы» set aside; kept in the URL so a reload keeps them. */
  parked?: InventoryFilters
}

export interface StorefrontUrlState {
  filters: InventoryFilters
  sort: { column: StorefrontSortColumn; direction: WarehouseSortDirection }
  page: number
  pageSize: number
  lifecycle: UnitLifecycleScope
  withPhotos: boolean
}

export const DEFAULT_PAGE_SIZE = 50
export const PAGE_SIZES = [25, 50, 100, 200] as const
// What the owner has to see first is what moved last, so the ungrouped table
// opens on the movement column newest first. A grouped table is ordered by the
// article the group is named after, which is the only key that level offers.
export const DEFAULT_SORT: WarehouseSort = { column: 'activity', direction: 'descending' }
export const GROUPED_DEFAULT_SORT: WarehouseSort = { column: 'article', direction: 'ascending' }

export function defaultSortFor(grouping: WarehouseGrouping): WarehouseSort {
  if (grouping === 'order') return { column: 'orderCreatedAt', direction: 'descending' }
  return grouping === 'none' ? DEFAULT_SORT : GROUPED_DEFAULT_SORT
}

/**
 * The column ids this page uses and their server keys.
 *
 * `activity` is the one whose two names differ - the column is the movement
 * column and the server key is the movement instant.
 */
const SERVER_SORT_COLUMNS: Record<PieceSortColumn, InventoryPageSort> = {
  uin: 'uin',
  article: 'article',
  title: 'title',
  product: 'article',
  weight: 'weight',
  lifecycle: 'lifecycle',
  activity: 'movement',
  size: 'size',
  photoCount: 'photo_count',
  onecPrice: 'onec_price',
  wildberriesPrice: 'storefront_price_wildberries',
  ymarketPrice: 'ymarket_showcase',
  ozonPrice: 'storefront_price_ozon',
  oriSitePrice: 'storefront_price_ori_site',
  ymarketSellsFor: 'ymarket_sells_for',
  ymarketPromo: 'ymarket_promo',
  ymarketBoost: 'ymarket_boost',
  ymarketRubPerGram: 'ymarket_price_per_gram',
  ymarketPurchaseCost: 'ymarket_purchase_cost',
  ymarketEarnings: 'ymarket_earnings',
  ymarketEarningsPercent: 'ymarket_earnings_percent',
  ymarketNetPerGram: 'ymarket_net_per_gram',
  ymarketPriceForTarget: 'ymarket_price_for_target',
  ymarketShowcase: 'ymarket_showcase',
  ymarketShowcasePerGram: 'ymarket_showcase_per_gram',
  ymarketShowcaseThresholdRatio: 'ymarket_showcase_threshold_ratio',
}

/** The order columns sort the order level (`/groups?level=order`), never the piece list. */
const ORDER_SORT_KEYS: Record<OrderSortColumn, InventoryOrderGroupSort> = {
  orderNumber: 'order_number',
  orderCreatedAt: 'order_created_at',
  buyer: 'buyer',
  orderStatus: 'order_status',
  city: 'city',
  orderAmount: 'order_amount',
}

function isOrderSortColumn(column: WarehouseSortColumn): column is OrderSortColumn {
  return column in ORDER_SORT_KEYS
}

export const MARKET_SORT_COLUMNS: readonly MarketSortColumn[] = ['ymarketSellsFor', 'ymarketPromo', 'ymarketBoost', 'ymarketRubPerGram', 'ymarketPurchaseCost', 'ymarketEarnings', 'ymarketEarningsPercent', 'ymarketNetPerGram', 'ymarketPriceForTarget', 'ymarketShowcase', 'ymarketShowcasePerGram', 'ymarketShowcaseThresholdRatio']
export const SORT_COLUMNS: readonly WarehouseSortColumn[] = ['uin', 'article', 'title', 'product', 'weight', 'lifecycle', 'activity', 'size', 'photoCount', 'onecPrice', 'wildberriesPrice', 'ymarketPrice', 'ozonPrice', 'oriSitePrice', ...MARKET_SORT_COLUMNS, 'orderNumber', 'orderCreatedAt', 'buyer', 'orderStatus', 'city', 'orderAmount']
export const GROUPINGS: readonly WarehouseGrouping[] = ['none', 'model', 'article', 'articleSize', 'order']

/** «По заказу» is the «Заказы» preset's own grouping; elsewhere it would be a second «Заказы». */
export function groupingsFor(view: ViewPresetKey): readonly WarehouseGrouping[] {
  return view === 'orders' ? GROUPINGS : GROUPINGS.filter((grouping) => grouping !== 'order')
}
export const DEFAULT_LIFECYCLE: UnitLifecycleScope = 'all'

// URL parameter names are part of the shareable deep link and are stable.
const PARAM = {
  search: 'q',
  material: 'material',
  fineness: 'fineness',
  productType: 'productType',
  shape: 'shape',
  techSize: 'size',
  manufacturer: 'manufacturer',
  marketplaces: 'marketplaces',
  presentOn: 'presentOn',
  absentOn: 'absentOn',
  readyFor: 'readyFor',
  localStock: 'stock',
  discrepancy: 'discrepancy',
  matchBucket: 'bucket',
  binding: 'binding',
  missingTaxonomy: 'noTaxonomy',
  stockMismatch: 'mismatch',
  missingAuthoritativeWeight: 'noWeight',
  staleOnly: 'stale',
  sort: 'sort',
  page: 'page',
  pageSize: 'perPage',
  variant: 'variant',
  grouping: 'group',
  lifecycle: 'lifecycle',
  withPhotos: 'withPhotos',
  withoutPhotos: 'withoutPhotos',
  unresolvedOnly: 'unresolvedOnly',
  missingMarking: 'missingMarking',
  unavailableOrStaleStock: 'unavailableOrStaleStock',
  problems: 'problems',
  descriptionPresence: 'description',
  rowGrain: 'rowGrain',
  view: 'view',
  include: 'include',
  orderId: 'orderId',
  orderProgram: 'orderProgram',
  orderStatus: 'orderStatus',
  orderCity: 'orderCity',
  ymarketEconomics: 'ymarket',
  ymarketNetPerGramMin: 'netPerGramMin',
  ymarketNetPerGramMax: 'netPerGramMax',
  ymarketPriceForTargetMin: 'targetPriceMin',
  ymarketPriceForTargetMax: 'targetPriceMax',
  ymarketShowcaseMin: 'showcaseFrom',
  ymarketShowcaseMax: 'showcaseTo',
  ymarketShowcasePerGramMin: 'showcasePerGramMin',
  ymarketShowcasePerGramMax: 'showcasePerGramMax',
  ymarketAttractiveness: 'attractiveness',
  ymarketBelowThreshold: 'belowThreshold',
  parked: 'parked',
} as const

/** A whole non-negative rouble amount as the API takes it, or nothing. */
function rubles(value: string | undefined): string | undefined {
  return value !== undefined && /^\d{1,9}$/.test(value) ? value : undefined
}

function text(params: URLSearchParams, name: string): string | undefined {
  const value = params.get(name)?.trim()
  return value ? value : undefined
}

function list(params: URLSearchParams, name: string): string[] | undefined {
  const values = (params.get(name) ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
  return values.length > 0 ? [...new Set(values)] : undefined
}

function repeatedList(params: URLSearchParams, name: string): string[] | undefined {
  const values = params.getAll(name)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean)
  return values.length > 0 ? [...new Set(values)] : undefined
}

function flag(params: URLSearchParams, name: string): boolean | undefined {
  return params.get(name) === '1' ? true : undefined
}

function orderPrograms(params: URLSearchParams): string[] | undefined {
  return repeatedList(params, PARAM.orderProgram)
}

/** Narrow a raw string to a member of a closed set, or drop it. */
export function parseMember<Schema extends z.ZodType>(schema: Schema, value: string | undefined): z.infer<Schema> | undefined {
  if (value === undefined) return undefined
  const result = schema.safeParse(value)
  return result.success ? result.data : undefined
}

function positiveInt(params: URLSearchParams, name: string, fallback: number): number {
  const raw = Number(params.get(name))
  return Number.isInteger(raw) && raw > 0 ? raw : fallback
}

function finiteNumber(params: URLSearchParams, name: string): number | undefined {
  const raw = params.get(name)
  if (raw === null || raw.trim() === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) && value >= 0 ? value : undefined
}

function positiveOrderId(params: URLSearchParams): number | null {
  const value = Number(params.get(PARAM.orderId))
  return Number.isSafeInteger(value) && value > 0 ? value : null
}

/** `stock=all`: the owner removed the preset's stock default, so a reload must not bring it back. */
export const EXPLICIT_ALL_STOCK = 'all'

/**
 * Saved links name filters that were merged into another control. Each maps
 * onto the one control that now owns the same fact; nothing is dropped.
 */
function migratedFilters(filters: InventoryFilters): InventoryFilters {
  const next: InventoryFilters = { ...filters }
  // The «Площадки» picker says «есть» per storefront (presentOn); a one-storefront
  // `marketplaces=` link meant the same. Several stay «хотя бы на одной».
  if (next.availableOn !== undefined && next.availableOn.length === 1) {
    next.presentOn = [...new Set([...(next.presentOn ?? []), ...next.availableOn])]
    next.availableOn = undefined
  }
  if (next.discrepancy === 'stock_divergence') {
    next.discrepancy = undefined
    next.stockMismatch = true
  }
  // «Не связано с площадкой» is «Связь с площадкой: нет»: one filter, one predicate.
  if (next.discrepancy === 'missing_binding') {
    next.discrepancy = undefined
    next.binding = next.binding ?? 'missing'
  }
  if (next.staleOnly && (next.discrepancy === undefined || next.discrepancy === 'stale_observation')) {
    next.staleOnly = undefined
    next.discrepancy = 'stale_observation'
  }
  if (next.rowGrain === 'unresolved_subject') {
    next.rowGrain = undefined
    next.unresolvedOnly = true
  }
  if (next.rowGrain === 'piece') next.rowGrain = undefined
  return next
}

function parseFilters(params: URLSearchParams): InventoryFilters {
  const requestedLifecycle = parseMember(UnitLifecycleScopeSchema, text(params, PARAM.lifecycle)) ?? DEFAULT_LIFECYCLE
  const requestedStock = parseMember(LocalStockStateSchema, text(params, PARAM.localStock))
  // «В наличии» has one spelling: the stock scope the presets write.
  const lifecycleIsStock = requestedLifecycle === 'in_stock' && (requestedStock === undefined || requestedStock === 'in_stock')
  return migratedFilters({
    search: text(params, PARAM.search),
    material: text(params, PARAM.material),
    fineness: text(params, PARAM.fineness),
    productType: text(params, PARAM.productType),
    shape: text(params, PARAM.shape),
    techSize: text(params, PARAM.techSize),
    manufacturer: text(params, PARAM.manufacturer),
    availableOn: list(params, PARAM.marketplaces),
    presentOn: repeatedList(params, PARAM.presentOn),
    absentOn: repeatedList(params, PARAM.absentOn),
    readyFor: repeatedList(params, PARAM.readyFor),
    localStock: lifecycleIsStock ? 'in_stock' : requestedStock,
    withoutPhotos: flag(params, PARAM.withoutPhotos),
    discrepancy: parseMember(InventoryDiscrepancyReasonSchema, text(params, PARAM.discrepancy)),
    matchBucket: parseMember(MatchBucketSchema, text(params, PARAM.matchBucket)),
    binding: parseMember(MarketplaceBindingSchema, text(params, PARAM.binding)),
    missingTaxonomy: parseMember(MissingTaxonomyFacetSchema, text(params, PARAM.missingTaxonomy)),
    stockMismatch: flag(params, PARAM.stockMismatch),
    missingAuthoritativeWeight: flag(params, PARAM.missingAuthoritativeWeight),
    staleOnly: flag(params, PARAM.staleOnly),
    unresolvedOnly: flag(params, PARAM.unresolvedOnly),
    missingMarking: flag(params, PARAM.missingMarking),
    unavailableOrStaleStock: flag(params, PARAM.unavailableOrStaleStock),
    problems: flag(params, PARAM.problems),
    descriptionPresence: parseMember(DescriptionPresenceSchema, text(params, PARAM.descriptionPresence)),
    rowGrain: parseMember(InventoryRowGrainSchema, text(params, PARAM.rowGrain)),
    orderProgram: orderPrograms(params),
    orderStatus: repeatedList(params, PARAM.orderStatus),
    orderCity: text(params, PARAM.orderCity),
    ymarketEconomics: parseMember(YmEconomicsFilterSchema, text(params, PARAM.ymarketEconomics)),
    ymarketNetPerGramMin: finiteNumber(params, PARAM.ymarketNetPerGramMin),
    ymarketNetPerGramMax: finiteNumber(params, PARAM.ymarketNetPerGramMax),
    ymarketPriceForTargetMin: finiteNumber(params, PARAM.ymarketPriceForTargetMin),
    ymarketPriceForTargetMax: finiteNumber(params, PARAM.ymarketPriceForTargetMax),
    ymarketShowcaseMin: rubles(text(params, PARAM.ymarketShowcaseMin)),
    ymarketShowcaseMax: rubles(text(params, PARAM.ymarketShowcaseMax)),
    ymarketShowcasePerGramMin: finiteNumber(params, PARAM.ymarketShowcasePerGramMin),
    ymarketAttractiveness: parseMember(YmPriceAttractivenessLevelSchema, text(params, PARAM.ymarketAttractiveness)),
    ymarketShowcasePerGramMax: finiteNumber(params, PARAM.ymarketShowcasePerGramMax),
    ymarketBelowThreshold: flag(params, PARAM.ymarketBelowThreshold),
  })
}

/** Only the warehouse-only filters can be parked, and only «Заказы» parks them. */
function parkedFilters(value: string | undefined): InventoryFilters | undefined {
  if (value === undefined) return undefined
  const parsed = parseFilters(new URLSearchParams(value))
  const parked: InventoryFilters = {}
  for (const key of WAREHOUSE_ONLY_FILTERS) {
    if (parsed[key] !== undefined) Object.assign(parked, { [key]: parsed[key] })
  }
  return activeFilterCount(parked) > 0 ? parked : undefined
}

export function parseWarehouseUrl(params: URLSearchParams): WarehouseUrlState {
  const requestedSize = positiveInt(params, PARAM.pageSize, DEFAULT_PAGE_SIZE)
  const pageSize = PAGE_SIZES.some((size) => size === requestedSize) ? requestedSize : DEFAULT_PAGE_SIZE
  const [column, direction] = (params.get(PARAM.sort) ?? '').split(':')
  const sortColumn = SORT_COLUMNS.find((candidate) => candidate === column)
  const requestedView = text(params, PARAM.view)
  const requestedGrouping = parseMember(InventoryGroupLevelSchema, text(params, PARAM.grouping))
  const namedView = isViewPresetKey(requestedView) ? requestedView : requestedView === RETIRED_YMARKET_PRICES_VIEW ? 'platforms' : 'actual'
  // An old link grouped by order outside «Заказы» opens «Заказы», where that grouping lives.
  const view = requestedGrouping === 'order' ? 'orders' : namedView
  const grouping = requestedGrouping ?? (view === 'orders' ? 'order' : 'none')
  const requestedLifecycle = parseMember(UnitLifecycleScopeSchema, text(params, PARAM.lifecycle)) ?? DEFAULT_LIFECYCLE
  const requestedStock = parseMember(LocalStockStateSchema, text(params, PARAM.localStock))
  const lifecycleIsStock = requestedLifecycle === 'in_stock' && (requestedStock === undefined || requestedStock === 'in_stock')
  const allowedSort: readonly WarehouseSortColumn[] = grouping === 'none'
    ? SORT_COLUMNS.filter((column) => !['orderNumber', 'orderCreatedAt', 'buyer', 'orderStatus', 'city', 'orderAmount'].includes(column))
    : grouping === 'order'
      ? ['orderNumber', 'orderCreatedAt', 'buyer', 'orderStatus', 'city', 'orderAmount']
      : ['article']
  const sort: WarehouseSort = sortColumn && allowedSort.includes(sortColumn)
    ? { column: sortColumn, direction: direction === 'desc' ? 'descending' : 'ascending' }
    : defaultSortFor(grouping)
  return {
    view,
    include: repeatedList(params, PARAM.include)?.flatMap((value) => {
      const parsed = InventoryIncludeFacetSchema.safeParse(value)
      return parsed.success ? [parsed.data] : []
    }) ?? [],
    filters: parseFilters(params),
    parked: view === 'orders' ? parkedFilters(text(params, PARAM.parked)) : undefined,
    sort,
    page: positiveInt(params, PARAM.page, 1),
    pageSize,
    selectedVariantId: text(params, PARAM.variant) ?? null,
    selectedOrderId: positiveOrderId(params),
    grouping,
    lifecycle: lifecycleIsStock ? DEFAULT_LIFECYCLE : requestedLifecycle,
    withPhotos: flag(params, PARAM.withPhotos) === true,
  }
}

/**
 * `presetDefaults` names the view's default filters: when the owner cleared the
 * preset's stock scope, `stock=all` records it, because a URL without `stock`
 * is a fresh open that applies the default again.
 */
export function serializeWarehouseUrl(
  state: WarehouseUrlState,
  presetDefaults?: (view: ViewPresetKey) => InventoryFilters,
): URLSearchParams {
  const params = new URLSearchParams()
  const { filters } = state
  const strings: [string, string | undefined][] = [
    [PARAM.search, filters.search],
    [PARAM.material, filters.material],
    [PARAM.fineness, filters.fineness],
    [PARAM.productType, filters.productType],
    [PARAM.shape, filters.shape],
    [PARAM.techSize, filters.techSize],
    [PARAM.manufacturer, filters.manufacturer],
    [PARAM.marketplaces, filters.availableOn?.join(',')],
    [PARAM.localStock, filters.localStock],
    [PARAM.discrepancy, filters.discrepancy],
    [PARAM.matchBucket, filters.matchBucket],
    [PARAM.binding, filters.binding],
    [PARAM.missingTaxonomy, filters.missingTaxonomy],
    [PARAM.orderCity, filters.orderCity],
    [PARAM.descriptionPresence, filters.descriptionPresence],
    [PARAM.ymarketEconomics, filters.ymarketEconomics],
    [PARAM.ymarketShowcaseMin, filters.ymarketShowcaseMin],
    [PARAM.ymarketShowcaseMax, filters.ymarketShowcaseMax],
    [PARAM.ymarketAttractiveness, filters.ymarketAttractiveness],
  ]
  for (const [name, value] of strings) if (value) params.set(name, value)
  for (const [name, value] of [
    [PARAM.ymarketNetPerGramMin, filters.ymarketNetPerGramMin],
    [PARAM.ymarketNetPerGramMax, filters.ymarketNetPerGramMax],
    [PARAM.ymarketPriceForTargetMin, filters.ymarketPriceForTargetMin],
    [PARAM.ymarketPriceForTargetMax, filters.ymarketPriceForTargetMax],
    [PARAM.ymarketShowcasePerGramMin, filters.ymarketShowcasePerGramMin],
    [PARAM.ymarketShowcasePerGramMax, filters.ymarketShowcasePerGramMax],
  ] as const) if (value !== undefined) params.set(name, String(value))
  if (filters.localStock === undefined && state.view !== undefined && presetDefaults?.(state.view).localStock !== undefined) {
    params.set(PARAM.localStock, EXPLICIT_ALL_STOCK)
  }
  for (const [name, values] of [
    [PARAM.presentOn, filters.presentOn],
    [PARAM.absentOn, filters.absentOn],
    [PARAM.readyFor, filters.readyFor],
    [PARAM.orderProgram, filters.orderProgram],
    [PARAM.orderStatus, filters.orderStatus],
  ] as const) {
    for (const value of new Set(values ?? [])) params.append(name, value)
  }
  if ((state.view ?? 'actual') !== 'actual') params.set(PARAM.view, state.view ?? 'actual')
  for (const value of new Set(state.include ?? [])) params.append(PARAM.include, value)
  if (filters.stockMismatch) params.set(PARAM.stockMismatch, '1')
  if (filters.missingAuthoritativeWeight) params.set(PARAM.missingAuthoritativeWeight, '1')
  if (filters.staleOnly) params.set(PARAM.staleOnly, '1')
  if (filters.unresolvedOnly) params.set(PARAM.unresolvedOnly, '1')
  if (filters.missingMarking) params.set(PARAM.missingMarking, '1')
  if (filters.unavailableOrStaleStock) params.set(PARAM.unavailableOrStaleStock, '1')
  if (filters.problems) params.set(PARAM.problems, '1')
  if (filters.ymarketBelowThreshold) params.set(PARAM.ymarketBelowThreshold, '1')
  if (filters.rowGrain) params.set(PARAM.rowGrain, filters.rowGrain)
  const defaultSort = defaultSortFor(state.grouping)
  if (state.sort.column !== defaultSort.column || state.sort.direction !== defaultSort.direction) {
    params.set(PARAM.sort, `${state.sort.column}:${state.sort.direction === 'descending' ? 'desc' : 'asc'}`)
  }
  if (state.page > 1) params.set(PARAM.page, String(state.page))
  if (state.pageSize !== DEFAULT_PAGE_SIZE) params.set(PARAM.pageSize, String(state.pageSize))
  if (state.selectedVariantId) params.set(PARAM.variant, state.selectedVariantId)
  if (state.selectedOrderId !== null) params.set(PARAM.orderId, String(state.selectedOrderId))
  if (state.grouping !== 'none') params.set(PARAM.grouping, state.grouping)
  // The default is intentionally explicit: it is a visible, clearable scope.
  params.set(PARAM.lifecycle, state.lifecycle)
  if (state.withPhotos) params.set(PARAM.withPhotos, '1')
  if (filters.withoutPhotos) params.set(PARAM.withoutPhotos, '1')
  if (state.view === 'orders' && state.parked !== undefined && activeFilterCount(state.parked) > 0) {
    const parked = filtersOnly(state.parked)
    parked.delete(PARAM.lifecycle)
    params.set(PARAM.parked, parked.toString())
  }
  return params
}

const STOREFRONT_SORT_COLUMNS: Record<StorefrontSortColumn, InventoryPageSort> = {
  uin: 'uin',
  article: 'article',
  title: 'title',
  weight: 'weight',
  lifecycle: 'lifecycle',
  activity: 'movement',
  size: 'size',
  photoCount: 'photo_count',
  onecPrice: 'onec_price',
  wildberriesPrice: 'storefront_price_wildberries',
  ymarketPrice: 'storefront_price_ymarket',
  ozonPrice: 'storefront_price_ozon',
  oriSitePrice: 'storefront_price_ori_site',
}
const STOREFRONT_SORT_COLUMN_NAMES: readonly StorefrontSortColumn[] = [
  'uin', 'article', 'title', 'weight', 'lifecycle', 'activity', 'size', 'photoCount',
  'onecPrice', 'wildberriesPrice', 'ymarketPrice', 'ozonPrice', 'oriSitePrice',
]

export const STOREFRONT_DEFAULT_SORT: StorefrontUrlState['sort'] = {
  column: 'article',
  direction: 'ascending',
}

export function parseStorefrontUrl(params: URLSearchParams): StorefrontUrlState {
  const warehouse = parseWarehouseUrl(params)
  const [column, direction] = (params.get(PARAM.sort) ?? '').split(':')
  const sortColumn = STOREFRONT_SORT_COLUMN_NAMES
    .find((candidate) => candidate === column)
  return {
    filters: {
      ...warehouse.filters,
      localStock: warehouse.filters.localStock ?? ('in_stock' satisfies LocalStockState),
    },
    sort: sortColumn
      ? { column: sortColumn, direction: direction === 'desc' ? 'descending' : 'ascending' }
      : STOREFRONT_DEFAULT_SORT,
    page: warehouse.page,
    pageSize: warehouse.pageSize,
    lifecycle: warehouse.lifecycle,
    withPhotos: warehouse.withPhotos,
  }
}

export function serializeStorefrontUrl(state: StorefrontUrlState): URLSearchParams {
  const params = serializeWarehouseUrl({
    filters: state.filters,
    sort: DEFAULT_SORT,
    page: state.page,
    pageSize: state.pageSize,
    selectedVariantId: null,
    selectedOrderId: null,
    grouping: 'none',
    lifecycle: state.lifecycle,
    withPhotos: state.withPhotos,
  })
  params.delete(PARAM.grouping)
  params.delete(PARAM.lifecycle)
  params.delete(PARAM.withPhotos)
  params.delete(PARAM.sort)
  if (state.sort.column !== STOREFRONT_DEFAULT_SORT.column || state.sort.direction !== STOREFRONT_DEFAULT_SORT.direction) {
    params.set(PARAM.sort, `${state.sort.column}:${state.sort.direction === 'descending' ? 'desc' : 'asc'}`)
  }
  return params
}

export function requestStorefrontFilters(state: StorefrontUrlState): InventoryFilters {
  return {
    ...state.filters,
    lifecycle: state.lifecycle,
    withPhotos: state.withPhotos || undefined,
    sort: STOREFRONT_SORT_COLUMNS[state.sort.column],
    direction: state.sort.direction === 'descending' ? 'desc' : 'asc',
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
  }
}

/**
 * The request, including server-supported ordering. The browser never orders
 * a delivered page; the inventory route owns the complete population order.
 */
export function requestFilters(state: WarehouseUrlState): InventoryFilters {
  const column = state.sort.column
  // An order column orders orders; the piece list beside them keeps its own default.
  const pieceSort = isOrderSortColumn(column) ? undefined : SERVER_SORT_COLUMNS[column]
  return {
    ...state.filters,
    lifecycle: state.lifecycle,
    withPhotos: state.withPhotos || undefined,
    include: state.include && state.include.length > 0 ? state.include : undefined,
    sort: pieceSort,
    direction: pieceSort === undefined ? undefined : state.sort.direction === 'descending' ? 'desc' : 'asc',
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
  }
}

/** The grouped read takes the same selectors; only the order level takes a sort. */
export function requestGroupFilters(state: WarehouseUrlState): InventoryGroupFilters {
  const column = state.sort.column
  const orderSort = state.grouping === 'order' && isOrderSortColumn(column) ? ORDER_SORT_KEYS[column] : undefined
  return {
    ...requestFilters(state),
    sort: undefined,
    orderSort,
    direction: orderSort === undefined ? undefined : state.sort.direction === 'descending' ? 'desc' : 'asc',
    lifecycle: state.lifecycle,
  }
}

export function requestUnitListingFilters(state: WarehouseUrlState): UnitListingFilters {
  const sortable = ['uin', 'article', 'title', 'weight', 'lifecycle'] as const
  const sort = sortable.find((member) => member === state.sort.column) ?? 'article'
  const { descriptionPresence: _descriptionPresence, ...unitFilters } = state.filters
  void _descriptionPresence
  return {
    ...unitFilters,
    withPhotos: state.withPhotos || undefined,
    lifecycle: state.lifecycle,
    sort,
    direction: state.sort.direction === 'descending' ? 'desc' : 'asc',
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
  }
}

export function changeGrouping(state: WarehouseUrlState, grouping: WarehouseGrouping): WarehouseUrlState {
  const allowed: readonly WarehouseSortColumn[] = grouping === 'none'
    ? ['uin', 'article', 'title', 'weight', 'lifecycle', 'activity', 'size', 'photoCount']
    : grouping === 'order'
      ? ['orderNumber', 'orderCreatedAt', 'buyer', 'orderStatus', 'city', 'orderAmount']
      : ['article']
  // Grouping is a view of the same population, so it never changes a filter.
  return {
    ...state,
    grouping,
    selectedOrderId: grouping === 'order' ? state.selectedOrderId : null,
    page: 1,
    sort: allowed.includes(state.sort.column)
      ? state.sort
      : defaultSortFor(grouping),
  }
}

export const EMPTY_FILTERS: InventoryFilters = {}

/** «Сбросить фильтры»: back to the preset's default filters; the toolbar search stays. */
export function resetFilters(presetFilters: InventoryFilters, current: InventoryFilters): InventoryFilters {
  return current.search === undefined ? { ...presetFilters } : { ...presetFilters, search: current.search }
}

/** The one «Статус» control: lifecycle names a published status, localStock the shelf scope. */
export type StatusScope = UnitLifecycleScope | 'out_of_stock' | 'unknown'
export const STATUS_SCOPES: readonly StatusScope[] = ['all', 'in_stock', 'in_transit', 'sold', 'out_of_stock', 'unknown']

/**
 * The server's `panelCount` key for one filter value: `name` for a flag, `name:value`
 * for a value. The count is what the list would total with that value chosen.
 */
export function panelCountKey(filters: InventoryFilters): string | null {
  const entries = Object.entries(filters).filter(([, value]) => value !== undefined)
  const only = entries.length === 1 ? entries[0] : undefined
  if (only === undefined) return null
  const [name, value] = only
  if (value === true) return name
  if (typeof value === 'string') return `${name}:${value}`
  return Array.isArray(value) && value.length === 1 && typeof value[0] === 'string' ? `${name}:${value[0]}` : null
}

export function storefrontPanelCountKey(code: string, state: 'present' | 'absent'): string {
  return panelCountKey(state === 'present' ? { presentOn: [code] } : { absentOn: [code] }) ?? code
}

export function statusPanelCountKey(scope: StatusScope): string {
  return `status:${scope}`
}

export function statusScopeOf(filters: InventoryFilters, lifecycle: UnitLifecycleScope): StatusScope {
  if (lifecycle !== 'all') return lifecycle
  return filters.localStock ?? 'all'
}

export function withStatusScope(filters: InventoryFilters, scope: StatusScope): { filters: InventoryFilters; lifecycle: UnitLifecycleScope } {
  if (scope === 'in_transit' || scope === 'sold' || scope === 'all') return { filters: { ...filters, localStock: undefined }, lifecycle: scope }
  return { filters: { ...filters, localStock: scope }, lifecycle: 'all' }
}

const STATUS_SCOPE_LABELS: Record<Exclude<UnitLifecycleScope, 'all'>, string> = {
  in_stock: warehouseProductStatusLabels.in_stock,
  in_transit: warehouseProductStatusLabels.in_transit,
  sold: warehouseProductStatusLabels.sold,
}

/**
 * Both scopes read the one published status, so «В наличии» and another status
 * select nothing together. The page says so instead of answering a silent zero.
 */
export function statusScopeConflict(filters: InventoryFilters, lifecycle: UnitLifecycleScope): string | null {
  if (filters.localStock !== 'in_stock' || (lifecycle !== 'in_transit' && lifecycle !== 'sold')) return null
  return `Статусы «${STATUS_SCOPE_LABELS[lifecycle]}» и «${STATUS_SCOPE_LABELS.in_stock}» исключают друг друга, поэтому изделий нет. Оставьте один из них.`
}

export function activeFilterCount(filters: InventoryFilters): number {
  return Object.entries(filters).filter(([name, value]) => (
    name !== 'limit' && name !== 'offset' && value !== undefined && value !== false && value !== ''
      && (!Array.isArray(value) || value.length > 0)
  )).length
}

export interface WarehousePreset {
  id: string
  label: string
  description: string
  filters: InventoryFilters
  /** The panel row that offers it: «Каждый день» or «Проблемы». */
  group: 'daily' | 'problems'
  /** The view presets whose filter panel offers it; absent means every preset. */
  scope?: readonly ViewPresetKey[]
}

// Warehouse filters narrow pieces on the shelf, not orders.
const WAREHOUSE_SCOPE: readonly ViewPresetKey[] = ['actual', 'platforms', 'ymarket_cheap']

/** The quick filters `view` offers. */
export function warehousePresetsFor(view: ViewPresetKey): readonly WarehousePreset[] {
  return WAREHOUSE_PRESETS.filter((preset) => preset.scope === undefined || preset.scope.includes(view))
}

// A preset is a named set of filter values, not a separate URL mode, so a
// shared link reproduces the same rows without carrying a preset name. Each
// fact has exactly one chip; the Market values share `ymarket=` and replace
// each other, as the `discrepancy=` values do.
export const WAREHOUSE_PRESETS: readonly WarehousePreset[] = [
  {
    id: 'ymarket-below-floor',
    label: 'Ниже порога ₽/г',
    description: 'Цена за грамм на Маркете ниже порога политики цен.',
    filters: { ymarketEconomics: 'below_floor' },
    group: 'daily',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'ymarket-no-cost',
    label: 'Нет закупки',
    description: 'Для изделия не записана закупочная стоимость 1С.',
    filters: { ymarketEconomics: 'no_cost' },
    group: 'daily',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'ymarket-in-promo',
    label: 'В акции',
    description: 'Изделие сейчас участвует в акции Маркета.',
    filters: { ymarketEconomics: 'in_promo' },
    group: 'daily',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'ymarket-boosted',
    label: 'С бустом',
    description: 'У изделия действует ставка буста Маркета.',
    filters: { ymarketEconomics: 'boosted' },
    group: 'daily',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'without-photos',
    label: 'Без фото',
    description: 'У изделия нет ни одной загруженной фотографии.',
    filters: { withoutPhotos: true },
    group: 'daily',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'stock-mismatch',
    label: 'Остатки расходятся',
    description: 'Остаток на складе и остаток на площадке расходятся числом.',
    filters: { stockMismatch: true },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'missing-weight',
    label: 'Нет веса',
    description: 'Вес изделия не измерен; вес упаковки им не считается.',
    filters: { missingAuthoritativeWeight: true },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'no-description',
    label: 'Нет описания',
    description: 'У изделия нет описания для площадок.',
    filters: { descriptionPresence: 'absent' },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'ymarket-drift',
    label: 'Цена расходится с Маркетом',
    description: 'Наша цена, зачёркнутая или минимум для акций расходятся с Маркетом, либо предложения на Маркете нет.',
    filters: { ymarketEconomics: 'drift' },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'ymarket-forge-price-not-set',
    label: 'Наша цена для Маркета не задана',
    description: 'Изделие продаётся на Маркете, а нашей цены для него ещё нет.',
    filters: { ymarketEconomics: 'forge_price_not_set' },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'ymarket-stale',
    label: 'Цены Маркета устарели',
    description: 'Последний сбор цен Маркета для изделия устарел.',
    filters: { ymarketEconomics: 'stale' },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'ymarket-outliers',
    label: 'Выброс по ₽/г',
    description: 'Цена за грамм выходит за обычный разброс выбранных изделий.',
    filters: { ymarketEconomics: 'outliers' },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'missing-binding',
    label: 'Не связано с площадкой',
    description: 'Изделие не привязано ни к одному предложению на площадках.',
    filters: { binding: 'missing' },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
  {
    id: 'price-divergence',
    label: 'Цена 1С расходится с площадкой',
    description: 'Цена в 1С и цена на площадке различаются.',
    filters: { discrepancy: 'price_divergence' },
    group: 'problems',
    scope: WAREHOUSE_SCOPE,
  },
]

/** The storefronts the «Площадки» picker names, in its order. */
export const STOREFRONT_CODES = ['wildberries', 'ymarket', 'ozon', 'ori_site'] as const
export type StorefrontCode = (typeof STOREFRONT_CODES)[number]
/** One storefront's condition: «есть» (presentOn), «нет» (absentOn), «готово к заливке» (readyFor). */
export type StorefrontState = 'any' | 'present' | 'absent' | 'ready'

export function storefrontStateOf(filters: InventoryFilters, code: string): StorefrontState {
  if (filters.readyFor?.includes(code)) return 'ready'
  if (filters.presentOn?.includes(code)) return 'present'
  if (filters.absentOn?.includes(code)) return 'absent'
  return 'any'
}

export function withStorefrontState(filters: InventoryFilters, code: string, state: StorefrontState): InventoryFilters {
  const toggled = (values: readonly string[] | undefined, add: boolean): string[] | undefined => {
    const next = (values ?? []).filter((value) => value !== code)
    if (add) next.push(code)
    return next.length > 0 ? next : undefined
  }
  return {
    ...filters,
    presentOn: toggled(filters.presentOn, state === 'present'),
    absentOn: toggled(filters.absentOn, state === 'absent'),
    readyFor: toggled(filters.readyFor, state === 'ready'),
  }
}

/** The filters «Заказы» does not take: they narrow pieces on the shelf, not orders. */
export const WAREHOUSE_ONLY_FILTERS = [
  'stockMismatch',
  'missingAuthoritativeWeight',
  'staleOnly',
  'discrepancy',
  'matchBucket',
  'binding',
  'missingTaxonomy',
  'rowGrain',
  'unresolvedOnly',
  'missingMarking',
  'unavailableOrStaleStock',
  'problems',
  'descriptionPresence',
  'withoutPhotos',
  'ymarketEconomics',
  'ymarketNetPerGramMin',
  'ymarketNetPerGramMax',
  'ymarketPriceForTargetMin',
  'ymarketPriceForTargetMax',
  'ymarketShowcaseMin',
  'ymarketShowcaseMax',
  'ymarketShowcasePerGramMin',
  'ymarketShowcasePerGramMax',
  'ymarketAttractiveness',
  'ymarketBelowThreshold',
] as const satisfies readonly (keyof InventoryFilters)[]

const ORDER_ONLY_FILTERS = ['orderProgram', 'orderStatus', 'orderCity'] as const satisfies readonly (keyof InventoryFilters)[]

export interface PresetSwitch {
  filters: InventoryFilters
  /** The warehouse filters set aside while «Заказы» is open; they return on leaving it. */
  parked: InventoryFilters
}

/**
 * Switching preset writes the next preset's defaults. Entering «Заказы» sets the
 * warehouse-only filters aside instead of dropping them, and leaving it brings
 * them back, so a round trip returns to the same filters.
 */
export function switchPresetFilters(
  current: InventoryFilters,
  next: ViewPresetKey,
  nextDefaults: InventoryFilters,
  parked: InventoryFilters,
): PresetSwitch {
  if (next === 'orders') {
    const aside: InventoryFilters = { ...parked }
    const kept: InventoryFilters = { ...current, ...nextDefaults, localStock: undefined }
    for (const key of WAREHOUSE_ONLY_FILTERS) {
      if (current[key] !== undefined) Object.assign(aside, { [key]: current[key] })
      Reflect.deleteProperty(kept, key)
    }
    return { filters: kept, parked: aside }
  }
  const defined = Object.fromEntries(Object.entries(current).filter(([, value]) => value !== undefined))
  const restored: InventoryFilters = { ...parked, ...defined, ...nextDefaults }
  for (const key of ORDER_ONLY_FILTERS) Reflect.deleteProperty(restored, key)
  return { filters: restored, parked: {} }
}

/** How the note on «Заказы» names the parked filters. */
export function parkedFilterLabels(parked: InventoryFilters): string[] {
  const named = WAREHOUSE_PRESETS.filter((preset) => isWarehousePresetActive(parked, preset))
  const covered = new Set(named.flatMap((preset) => Object.keys(preset.filters)))
  const rest = WAREHOUSE_ONLY_FILTERS.some((key) => parked[key] !== undefined && !covered.has(key))
  return [...named.map((preset) => `«${preset.label}»`), ...(rest ? ['фильтры «Для сверки»'] : [])]
}

function filtersOnly(filters: InventoryFilters): URLSearchParams {
  return serializeWarehouseUrl({
    filters,
    sort: DEFAULT_SORT,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    selectedVariantId: null,
    selectedOrderId: null,
    grouping: 'none',
    lifecycle: DEFAULT_LIFECYCLE,
    withPhotos: false,
  })
}

function normalized(filters: InventoryFilters): string {
  const params = filtersOnly(filters)
  params.sort()
  return params.toString()
}

/** The URL parameter names a set of filters writes, e.g. a view preset's default filters. */
export function filterParamNames(filters: InventoryFilters): string[] {
  const base = filtersOnly(EMPTY_FILTERS)
  return [...new Set(filtersOnly(filters).keys())].filter((name) => !base.has(name))
}

export function activePresetId(filters: InventoryFilters): string | null {
  const current = normalized(filters)
  return WAREHOUSE_PRESETS.find((preset) => normalized(preset.filters) === current)?.id ?? null
}

function ownParams(preset: WarehousePreset): URLSearchParams {
  return filtersOnly(preset.filters)
}

/** A chip is on when the filters hold its own values, whatever else is set. */
export function isWarehousePresetActive(
  filters: InventoryFilters,
  preset: WarehousePreset,
): boolean {
  const current = filtersOnly(filters)
  return filterParamNames(preset.filters).every((name) => current.get(name) === ownParams(preset).get(name))
}

function withoutPreset(filters: InventoryFilters, preset: WarehousePreset): InventoryFilters {
  const next: InventoryFilters = { ...filters }
  for (const key of Object.keys(preset.filters)) {
    if (key in next) Reflect.deleteProperty(next, key)
  }
  return next
}

export function togglePreset(filters: InventoryFilters, presetId: string): InventoryFilters {
  const preset = WAREHOUSE_PRESETS.find((candidate) => candidate.id === presetId)
  if (!preset) return filters
  // A chip writes only its own keys: the preset default, the search and every
  // other filter stay as they are.
  return isWarehousePresetActive(filters, preset) ? withoutPreset(filters, preset) : { ...filters, ...preset.filters }
}
